import React from "react";
import { Button, Grid, MenuItem, Typography } from "@mui/material";
import { Field, Form, Formik, FormikProps } from "formik";
import { TextField } from "formik-mui";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { findIndex } from "lodash-es";

import { APIError, PointSource, Substance, type Timefactor } from "src/types";
import { adornments } from "src/config/adornments";
import { pointSources as decimalScale } from "src/config/decimal-scales";
import { usePointSourceSchema } from "src/schemas/point-source.schema";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { DecimalScaleTextField } from "src/components/form-components/DecimalScaleTextField";
import { ButtonDocs } from "src/components/ButtonDocs";
import { defaultTransComponents } from "src/config/i18n";

export type PointSourceFormData = Omit<PointSource, "id">;

interface Props {
  title?: string;
  readOnly?: boolean;
  data: PointSourceFormData;
  substances: Substance[];
  timefactors: Timefactor[];
  onClose: () => void;
  onSubmit: (values: PointSourceFormData) => any;
  onDelete?: () => void;
  isLoading: boolean;
  errors?: APIError[] | null;
  formRef?: React.MutableRefObject<FormikProps<PointSourceFormData> | null>;
}

export function PointSourceForm(props: Props) {
  const { t } = useTranslation();
  const validationSchema = usePointSourceSchema();
  const initialValues: PointSourceFormData = {
    name: props.data.name,
    height_m: props.data.height_m,
    diameter_m: props.data.diameter_m,
    temperature_K: props.data.temperature_K,
    flowrate_Nm3_s: props.data.flowrate_Nm3_s,
    nonox: props.data.nonox,
    emissions_kg_h: props.data.emissions_kg_h,
    ifdmTimeFactor: props.data.ifdmTimeFactor,
  };
  return render();

  function render() {
    return (
      <Formik
        innerRef={props.formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values: PointSourceFormData) => {
          return props.onSubmit(values);
        }}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              {props.title && (
                <Grid item xs={12}>
                  <Typography variant="h2">{props.title}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <ButtonDocs docsPath="/file-formats/point-source-emission-file">
                  {t("label.moreInfoAboutWILDCARD")}
                </ButtonDocs>
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="name"
                  label={t("label.name")}
                  type="string"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>
              {props.substances.map((substance) => {
                const substanceIndex = findIndex(props.data.emissions_kg_h, {
                  substance: substance.key,
                });
                return (
                  <Grid item xs={12} key={["emissions_kg_h", substance.key].join("-")}>
                    <Field
                      component={DecimalScaleTextField}
                      decimalScale={decimalScale.emissions_kg_h}
                      name={`emissions_kg_h[${substanceIndex}].emission_kg_h`}
                      label={
                        <Trans
                          i18nKey="label.emissionsSubstance"
                          components={defaultTransComponents}
                          values={{
                            substance: substance.name,
                          }}
                        />
                      }
                      type="number"
                      variant="outlined"
                      fullWidth
                      InputProps={adornments(t).kgPerHour}
                      disabled={props.readOnly}
                      inputProps={{ step: "any" }}
                    />
                  </Grid>
                );
              })}

              <Grid item xs={12}>
                <Field
                  component={DecimalScaleTextField}
                  decimalScale={decimalScale.nonox}
                  name="nonox"
                  label={t("label.NOOverNOXRatio")}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                  inputProps={{ step: "any" }}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={DecimalScaleTextField}
                  decimalScale={decimalScale.height_m}
                  name="height_m"
                  label={t("label.height")}
                  type="number"
                  variant="outlined"
                  InputProps={adornments(t).meter}
                  fullWidth
                  disabled={props.readOnly}
                  inputProps={{ step: "any" }}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={DecimalScaleTextField}
                  decimalScale={decimalScale.flowrate_Nm3_s}
                  name="flowrate_Nm3_s"
                  label={t("label.flowrate")}
                  type="number"
                  variant="outlined"
                  InputProps={adornments(t).nm3PerSecond}
                  fullWidth
                  disabled={props.readOnly}
                  inputProps={{ step: "any" }}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={DecimalScaleTextField}
                  decimalScale={decimalScale.diameter_m}
                  name="diameter_m"
                  label={t("label.diameter")}
                  type="number"
                  variant="outlined"
                  InputProps={adornments(t).meter}
                  fullWidth
                  disabled={props.readOnly}
                  inputProps={{ step: "any" }}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={DecimalScaleTextField}
                  decimalScale={decimalScale.temperature_K}
                  name="temperature_K"
                  label={t("label.temperature")}
                  type="number"
                  variant="outlined"
                  InputProps={adornments(t).kelvin}
                  fullWidth
                  disabled={props.readOnly}
                  inputProps={{ step: "any" }}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="ifdmTimeFactor"
                  label={t("label.timefactor")}
                  type="text"
                  select={true}
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                >
                  <MenuItem value={""}>
                    <em>{t("label.timefactor_placeholder")}</em>
                  </MenuItem>
                  {props.timefactors.map((timefactor) => (
                    <MenuItem key={timefactor.id} value={timefactor.nameKey}>
                      {timefactor.nameTranslation}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>

              {props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}

              {!props.readOnly && (
                <>
                  <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                    <Button variant="text" onClick={props.onClose}>
                      {t("label.cancel")}
                    </Button>
                    <ButtonLoading
                      isLoading={isSubmitting || props.isLoading}
                      type="submit"
                      color="primary"
                      variant="contained"
                    >
                      {t("label.save")}
                    </ButtonLoading>
                  </ButtonGrid>

                  <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                    {props.onDelete && (
                      <DeleteButton variant="text" onClick={props.onDelete}>
                        {t("label.delete")}
                      </DeleteButton>
                    )}
                  </ButtonGrid>
                </>
              )}

              {props.readOnly && (
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={props.onClose}>
                    {t("label.close")}
                  </Button>
                </ButtonGrid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}

const DeleteButton = styled(Button)`
  color: ${({ theme }) => theme.palette.error.main};
`;
